import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '../components/auth/AuthContext';
import {
  getSessionTimeoutMs,
  getSessionWarningLeadMs,
  SESSION_ACTIVITY_EVENTS,
} from '../components/auth/sessionTimeoutConstants';

const formatCountdown = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const useSessionTimeout = () => {
  const { extendSession, handleSessionExpired, isAuthenticated, isInitialized, isLoggingOut } = useAuth();
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const isExtendingRef = useRef(false);
  const isExpiredHandlingRef = useRef(false);

  const sessionTimeoutMs = useMemo(() => getSessionTimeoutMs(), []);
  const warningLeadMs = useMemo(() => getSessionWarningLeadMs(), []);
  const warningThresholdMs = sessionTimeoutMs - warningLeadMs;
  const warningLeadMinutes = Math.max(1, Math.round(warningLeadMs / 60000));

  const enabled = isInitialized && isAuthenticated && !isLoggingOut;

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const continueSession = useCallback(async () => {
    if (isExtendingRef.current) {
      return;
    }

    isExtendingRef.current = true;
    try {
      const success = await extendSession();
      if (success) {
        setIsWarningVisible(false);
        setCountdownSeconds(0);
        resetActivity();
        isExpiredHandlingRef.current = false;

        return;
      }

      if (!isExpiredHandlingRef.current) {
        isExpiredHandlingRef.current = true;
        await handleSessionExpired();
      }
    } finally {
      isExtendingRef.current = false;
    }
  }, [extendSession, handleSessionExpired, resetActivity]);

  useEffect(() => {
    if (!enabled) {
      setIsWarningVisible(false);
      setCountdownSeconds(0);
      isExpiredHandlingRef.current = false;
      isExtendingRef.current = false;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    resetActivity();

    const handleActivity = () => {
      if (!isWarningVisible) {
        resetActivity();
      }
    };

    SESSION_ACTIVITY_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, handleActivity, { passive: true });
    });

    return () => {
      SESSION_ACTIVITY_EVENTS.forEach((eventName) => {
        document.removeEventListener(eventName, handleActivity);
      });
    };
  }, [enabled, isWarningVisible, resetActivity]);

  useEffect(() => {
    if (!enabled || isWarningVisible) {
      return;
    }

    const checkIdle = () => {
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs >= warningThresholdMs) {
        setIsWarningVisible(true);
        setCountdownSeconds(Math.ceil(warningLeadMs / 1000));
      }
    };

    const intervalId = window.setInterval(checkIdle, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [enabled, isWarningVisible, warningLeadMs, warningThresholdMs]);

  useEffect(() => {
    if (!enabled || !isWarningVisible || countdownSeconds <= 0) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setCountdownSeconds((current) => Math.max(0, current - 1));
    }, 1000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [countdownSeconds, enabled, isWarningVisible]);

  useEffect(() => {
    if (!enabled || !isWarningVisible || countdownSeconds > 0) {
      return;
    }

    if (!isExpiredHandlingRef.current) {
      isExpiredHandlingRef.current = true;
      void handleSessionExpired();
    }
  }, [countdownSeconds, enabled, handleSessionExpired, isWarningVisible]);

  return {
    continueSession,
    countdownLabel: formatCountdown(countdownSeconds),
    countdownSeconds,
    isWarningVisible,
    warningLeadMinutes,
  };
};
